import { useMemo, useState } from 'react';
import { communications, documents, parties, requests } from '../../api/endpoints';
import type {
    Attachment, ChannelAccount, ClientAccount, Conversation, ConversationRoute,
    FileBlob, Message, MessageRequest, RequestEntity,
} from '../../api/types';
import { FilePreviewModal } from '../../components/FilePreviewModal';
import {
    Card, ErrorBox, Field, fmtDate, KV, shortId, StatusBadge,
} from '../../components/ui';
import { navigate, useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';

const CURRENCIES = ['RUB', 'USD', 'EUR', 'CNY'];

const DIRECTION_LABELS: Record<string, string> = {
  inbound: 'Входящее',
  outbound: 'Исходящее',
};

function messageTime(m: Message): string {
  return fmtDate(m.sent_at ?? m.created_at);
}

function attachmentName(a: Attachment): string {
  return a.original_name ?? a.filename ?? a.name ?? shortId(a.file_blob ?? a.id);
}

/** Панель маршрута одного диалога: активная заявка, смена маршрута, новая заявка. */
function ConversationRoutePanel({ conversation, channelMap, onChanged }: {
  conversation: Conversation;
  channelMap: Map<string, ChannelAccount>;
  onChanged: () => void;
}) {
  const routes = useAsync<ConversationRoute[]>(
    () => communications.conversationRoutes({ conversation: conversation.id }),
    [conversation.id],
  );
  const allRequests = useAsync<RequestEntity[]>(() => requests.list(), []);
  const accounts = useAsync<ClientAccount[]>(() => parties.clientAccounts(), []);

  const [routeTarget, setRouteTarget] = useState('');
  const [linkErr, setLinkErr] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newAccountId, setNewAccountId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newCurrency, setNewCurrency] = useState('RUB');

  const activeRoute = useMemo(() => {
    const list = (routes.data ?? []).filter((r) => !r.active_to);
    list.sort((a, b) => String(b.active_from ?? '').localeCompare(String(a.active_from ?? '')));
    return list[0] ?? null;
  }, [routes.data]);

  const activeRequest = useAsync<RequestEntity | null>(
    () => (activeRoute ? requests.get(activeRoute.request).catch(() => null) : Promise.resolve(null)),
    [activeRoute?.request],
  );

  const channel = conversation.channel ? channelMap.get(conversation.channel) : undefined;
  const peer = conversation.peer_address
    ?? channel?.mailbox_or_number
    ?? conversation.external_thread_id
    ?? shortId(conversation.id);
  const convType = conversation.type === 'supplier' ? 'поставщик' : 'клиент';
  const channelType = channel?.type ?? conversation.channel_type ?? '';

  const applyRoute = async (targetId: string) => {
    if (!targetId) return;
    setBusy(true); setLinkErr(null); setNotice('');
    try {
      await communications.routeConversation(conversation.id, targetId);
      setNotice('Маршрут диалога обновлён.');
      routes.reload();
      onChanged();
    } catch (x) { setLinkErr(x); } finally { setBusy(false); }
  };

  const createAndRoute = async () => {
    if (!newAccountId || !newTitle) return;
    setBusy(true); setLinkErr(null);
    try {
      const res = await requests.create({
        client_account_id: newAccountId,
        title: newTitle,
        currency: newCurrency,
      });
      if (res?.request_id) {
        try { await communications.routeConversation(conversation.id, res.request_id); } catch { /* маршрут выставит decide/auto-route */ }
        navigate(`/requests/${res.request_id}`);
      }
    } catch (x) { setLinkErr(x); } finally { setBusy(false); }
  };

  return (
    <Card title={`Диалог: ${peer}`}
          actions={<span className="badge muted">{channelType || convType}</span>}>
      <div className="kv-grid" style={{ marginBottom: 8 }}>
        <KV k="Активный маршрут" v={
          activeRoute
            ? <a href={`#/requests/${activeRoute.request}`}>{activeRequest.data?.number ?? shortId(activeRoute.request)}</a>
            : routes.loading ? '…' : 'не назначен'
        } />
        <KV k="Режим" v={activeRoute?.mode === 'manual' ? 'вручную' : 'авто'} />
      </div>
      <ErrorBox error={routes.error} onRetry={routes.reload} />
      <ErrorBox error={linkErr} />
      {notice && <div className="alert alert-info">{notice}</div>}
      <div className="form-row" style={{ alignItems: 'flex-end' }}>
        <Field label="Сменить маршрут на заявку">
          <select value={routeTarget} onChange={(e) => setRouteTarget(e.target.value)}>
            <option value="">— выберите заявку —</option>
            {(allRequests.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>{r.number ?? shortId(r.id)}</option>
            ))}
          </select>
        </Field>
        <button className="btn" disabled={busy || !routeTarget}
                onClick={() => void applyRoute(routeTarget)}>
          Сменить маршрут
        </button>
        <button className="btn" onClick={() => setShowNew((v) => !v)}>
          + Новая заявка
        </button>
      </div>
      {showNew && (
        <div className="inline-form">
          <div className="form-row">
            <Field label="Клиентский счёт">
              <select value={newAccountId} onChange={(e) => setNewAccountId(e.target.value)}>
                <option value="">— выберите —</option>
                {(accounts.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>{a.party_name ?? a.party}</option>
                ))}
              </select>
            </Field>
            <Field label="Название первой позиции">
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
            </Field>
            <Field label="Валюта">
              <select value={newCurrency} onChange={(e) => setNewCurrency(e.target.value)}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <div className="form-actions">
            <button className="btn primary" disabled={busy || !newAccountId || !newTitle}
                    onClick={() => void createAndRoute()}>
              {busy ? 'Создание…' : 'Создать и назначить маршрутом'}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

/** Вкладка «Переписка»: сообщения заявки, маршруты диалогов, ответ клиенту. */
export function MessagesTab({ data }: { data: CardData }) {
  const requestId = data.request.id;

  const links = useAsync<MessageRequest[]>(
    () => communications.messageLinks({ request: requestId }).catch(() => [] as MessageRequest[]),
    [requestId],
  );
  const msgs = useAsync<Message[]>(
    () => communications.messages({ request: requestId }).catch(() => [] as Message[]),
    [requestId],
  );
  const atts = useAsync<Attachment[]>(
    () => documents.attachments().catch(() => [] as Attachment[]),
    [],
  );
  const blobs = useAsync<FileBlob[]>(
    () => documents.fileBlobs({ request: requestId }).catch(() => [] as FileBlob[]),
    [requestId],
  );
  const convs = useAsync<Conversation[]>(
    () => communications.conversations().catch(() => [] as Conversation[]),
    [],
  );
  const accounts = useAsync<ChannelAccount[]>(
    () => communications.channelAccounts().catch(() => [] as ChannelAccount[]),
    [],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [linkTarget, setLinkTarget] = useState(requestId);
  const [linkErr, setLinkErr] = useState<unknown>(null);
  const [linkNotice, setLinkNotice] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);

  const [replyBody, setReplyBody] = useState('');
  const [replyAtts, setReplyAtts] = useState<Set<string>>(new Set());
  const [replyErr, setReplyErr] = useState<unknown>(null);
  const [replyNotice, setReplyNotice] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState<Set<string>>(new Set());
  const [resendErr, setResendErr] = useState<unknown>(null);
  const [resendNotice, setResendNotice] = useState('');
  const [relinkFor, setRelinkFor] = useState<string | null>(null);
  const [relinkTarget, setRelinkTarget] = useState('');
  const [relinkBusy, setRelinkBusy] = useState(false);
  const [previewIdx, setPreviewIdx] = useState<{ items: { blobId: string; name: string }[]; index: number } | null>(null);
  const [dropped, setDropped] = useState<Array<{
    file: File; progress: number; status: 'uploading' | 'checking' | 'ready' | 'error';
    blobId?: string; error?: string;
  }>>([]);
  const [dragOver, setDragOver] = useState(false);

  const channelMap = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, a])),
    [accounts.data],
  );

  const messages = useMemo(() => {
    const all = msgs.data ?? [];
    const linked = new Set((links.data ?? []).map((l) => l.message));
    const filtered = linked.size > 0
      ? all.filter((m) => linked.has(m.id) || (m.requests ?? []).includes(requestId))
      : all;
    return [...filtered].sort((a, b) =>
      String(a.sent_at ?? a.created_at ?? '').localeCompare(String(b.sent_at ?? b.created_at ?? '')));
  }, [msgs.data, links.data, requestId]);

  const attachmentsByMessage = useMemo(() => {
    const map = new Map<string, Attachment[]>();
    for (const a of atts.data ?? []) {
      if (!a.message) continue;
      const arr = map.get(a.message) ?? [];
      arr.push(a);
      map.set(a.message, arr);
    }
    return map;
  }, [atts.data]);

  const conversations = useMemo(() => {
    const ids = new Set(messages.map((m) => m.conversation).filter(Boolean) as string[]);
    return (convs.data ?? []).filter((c) => ids.has(c.id));
  }, [convs.data, messages]);

  const reloadAll = () => { msgs.reload(); links.reload(); atts.reload(); convs.reload(); };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const linkSelected = async () => {
    if (!linkTarget || selected.size === 0) return;
    setLinkBusy(true); setLinkErr(null); setLinkNotice('');
    try {
      for (const mid of selected) {
        await communications.linkRequest(mid, linkTarget);
      }
      setLinkNotice(`Привязано сообщений: ${selected.size}.`);
      setSelected(new Set());
      links.reload(); msgs.reload();
    } catch (x) { setLinkErr(x); } finally { setLinkBusy(false); }
  };

  const allRequests = useAsync<RequestEntity[]>(() => requests.list(), []);

  const relink = async (toRequestId?: string) => {
    if (!relinkFor) return;
    setRelinkBusy(true); setLinkErr(null);
    try {
      await communications.relinkMessage(relinkFor, toRequestId);
      setRelinkFor(null); setRelinkTarget('');
      setLinkNotice(toRequestId ? 'Привязка исправлена — сообщение перенесено.' : 'Сообщение возвращено во входящие.');
      links.reload(); msgs.reload();
    } catch (x) { setLinkErr(x); } finally { setRelinkBusy(false); }
  };

  const resend = async (messageId: string) => {
    setResendErr(null); setResendNotice('');
    setResendBusy((prev) => new Set(prev).add(messageId));
    try {
      const r = await communications.resendMessage(messageId);
      setResendNotice(r?.requeued === false
        ? 'Отправка уже в очереди или завершена.'
        : 'Письмо повторно поставлено в очередь отправки.');
      // статус обновит воркер — подтянуть через пару секунд.
      window.setTimeout(() => { msgs.reload(); }, 4000);
      msgs.reload();
    } catch (x) { setResendErr(x); } finally {
      setResendBusy((prev) => { const n = new Set(prev); n.delete(messageId); return n; });
    }
  };

  // --- Перетаскивание вложений в форму ответа ---
  const MAX_ATTACHMENTS = 10;
  const MAX_FILE_MB = 25;

  const updateDropped = (file: File, patch: Partial<(typeof dropped)[number]>) => {
    setDropped((prev) => prev.map((e) => (e.file === file ? { ...e, ...patch } : e)));
  };

  const pollScan = (file: File, blobId: string) => {
    const check = async () => {
      try {
        const blob = await documents.getFileBlob(blobId);
        const st = blob?.scan_status ?? '';
        if (st === 'clean') { updateDropped(file, { status: 'ready' }); return; }
        if (st === 'infected' || st === 'failed' || st === 'error') {
          updateDropped(file, { status: 'error', error: 'Файл не прошёл проверку' });
          return;
        }
      } catch { /* транспортная ошибка — повтор */ }
      window.setTimeout(() => void check(), 2000);
    };
    void check();
  };

  const uploadDropped = async (file: File) => {
    const { ensureCsrf } = await import('../../api/client');
    const token = await ensureCsrf();
    const form = new FormData();
    form.append('file', file);
    form.append('source_type', 'request');
    form.append('request_id', requestId);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/v1/documents/file-blob/upload/');
    xhr.withCredentials = true;
    if (token) xhr.setRequestHeader('X-CSRFToken', token);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        updateDropped(file, { progress: Math.round((ev.loaded / ev.total) * 100) });
      }
    };
    xhr.onload = () => {
      try {
        if (xhr.status === 200 || xhr.status === 201) {
          const data = JSON.parse(xhr.responseText) as { id?: string };
          updateDropped(file, { status: 'checking', progress: 100, blobId: data.id });
          if (data.id) pollScan(file, data.id);
          return;
        }
        const data = JSON.parse(xhr.responseText) as { message?: string };
        updateDropped(file, { status: 'error', error: data.message || `HTTP ${xhr.status}` });
      } catch {
        updateDropped(file, { status: 'error', error: `HTTP ${xhr.status}` });
      }
    };
    xhr.onerror = () => updateDropped(file, { status: 'error', error: 'Сеть недоступна' });
    xhr.send(form);
  };

  const addDropped = (files: FileList | File[]) => {
    const room = Math.max(0, MAX_ATTACHMENTS - dropped.length - replyAtts.size);
    const entries = Array.from(files).slice(0, room).map((file) => ({
      file, progress: 0,
      status: (file.size > MAX_FILE_MB * 1024 * 1024 ? 'error' : 'uploading') as 'error' | 'uploading',
      error: file.size > MAX_FILE_MB * 1024 * 1024 ? `более ${MAX_FILE_MB} МБ` : undefined,
      blobId: undefined as string | undefined,
    }));
    if (Array.from(files).length > room) {
      setReplyErr(new Error(`Можно прикрепить не более ${MAX_ATTACHMENTS} файлов — лишние не добавлены.`));
    }
    setDropped((prev) => [...prev, ...entries]);
    entries.filter((e) => e.status === 'uploading')
      .forEach((e) => void uploadDropped(e.file));
  };

  const removeDropped = (file: File) => {
    setDropped((prev) => prev.filter((e) => e.file !== file));
  };

  const retryDropped = (file: File) => {
    setDropped((prev) => prev.filter((e) => e.file !== file));
    setDropped((prev) => [...prev, { file, progress: 0, status: 'uploading' }]);
    void uploadDropped(file);
  };

  const droppedPending = dropped.some((d) => d.status === 'uploading' || d.status === 'checking');
  const droppedReadyIds = dropped.filter((d) => d.status === 'ready' && d.blobId).map((d) => d.blobId as string);

  const sendReply = async () => {
    if (!replyBody.trim() || droppedPending) return;
    setReplyBusy(true); setReplyErr(null); setReplyNotice('');
    try {
      await communications.sendMessage({
        request_id: requestId,
        body: replyBody,
        attachment_ids: [...replyAtts, ...droppedReadyIds],
      });
      setReplyNotice('Ответ отправлен в канал.');
      setReplyBody('');
      setReplyAtts(new Set());
      setDropped([]);
      msgs.reload();
    } catch (x) { setReplyErr(x); } finally { setReplyBusy(false); }
  };

  return (
    <div>
      {conversations.map((c) => (
        <ConversationRoutePanel key={c.id} conversation={c} channelMap={channelMap}
                                onChanged={reloadAll} />
      ))}

      <Card title="Сообщения заявки" actions={<button className="btn" onClick={reloadAll}>Обновить</button>}>
        <ErrorBox error={msgs.error} onRetry={msgs.reload} />
        <ErrorBox error={linkErr} />
        <ErrorBox error={resendErr} />
        {linkNotice && <div className="alert alert-info">{linkNotice}</div>}
        {resendNotice && <div className="alert alert-info">{resendNotice}</div>}
        {messages.length === 0 && !msgs.loading && (
          <div className="empty">Сообщений по заявке нет</div>
        )}
        {msgs.loading && !msgs.data && <div className="loading">Загрузка переписки…</div>}
        {messages.map((m) => {
          const mAtts = [
            ...(attachmentsByMessage.get(m.id) ?? []),
            ...(m.attachments ?? []).filter((a) => !attachmentsByMessage.get(m.id)?.some((x) => x.id === a.id)),
          ];
          return (
            <div key={m.id} className={`msg-item ${m.direction ?? ''}`}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <input type="checkbox" aria-label="Выбрать сообщение"
                       checked={selected.has(m.id)} onChange={() => toggle(m.id)}
                       style={{ marginTop: 3 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className={`badge ${m.direction === 'outbound' ? 'info' : 'muted'}`}>
                      {DIRECTION_LABELS[m.direction ?? ''] ?? m.direction ?? '—'}
                    </span>
                    <strong>{m.sender || '—'}</strong>
                    <span className="muted">{messageTime(m)}</span>
                    {m.direction === 'outbound' && <StatusBadge value={m.delivery_status} />}
                  </div>
                  {m.direction === 'outbound' && m.delivery_error && (
                    <div className="alert alert-error" style={{ marginTop: 4, fontSize: 13 }}>
                      Не отправлено: {m.delivery_error}
                    </div>
                  )}
                  {m.direction === 'outbound' && m.can_resend && (
                    <button className="btn small" style={{ marginTop: 4 }}
                            disabled={resendBusy.has(m.id)}
                            onClick={() => void resend(m.id)}>
                      {resendBusy.has(m.id) ? 'Повтор…' : 'Повторить отправку'}
                    </button>
                  )}
                  {m.subject && <div style={{ marginTop: 4 }}><strong>{m.subject}</strong></div>}
                  {m.body && <div className="msg-body">{m.body}</div>}
                  {(m.link_info ?? []).length > 0 && (
                    <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                      {(m.link_info ?? []).map((li, i) => (
                        <span key={i} style={{ marginRight: 10 }}>
                          {li.link_reason || 'Привязано'}
                          {li.linked_by ? ` · ${li.linked_by}` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                  {mAtts.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      {mAtts.map((a, i) => (
                        <button key={a.id} className="btn small" style={{ marginRight: 6 }}
                                disabled={!a.file_blob}
                                onClick={() => setPreviewIdx({
                                  items: mAtts.filter((x) => x.file_blob)
                                    .map((x) => ({ blobId: String(x.file_blob), name: attachmentName(x) })),
                                  index: mAtts.filter((x) => x.file_blob).indexOf(a),
                                })}>
                          📎 {attachmentName(a)}
                        </button>
                      ))}
                    </div>
                  )}
                  {m.direction === 'inbound' && (m.requests ?? []).length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <button className="btn small" onClick={() => setRelinkFor(relinkFor === m.id ? null : m.id)}>
                        Исправить привязку
                      </button>
                      {relinkFor === m.id && (
                        <span className="inline-form" style={{ marginLeft: 8, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <select value={relinkTarget} onChange={(e) => setRelinkTarget(e.target.value)}>
                            <option value="">— заявка —</option>
                            {(allRequests.data ?? []).filter((r) => r.id !== requestId).map((r) => (
                              <option key={r.id} value={r.id}>{r.number ?? shortId(r.id)}</option>
                            ))}
                          </select>
                          <button className="btn small primary" disabled={relinkBusy || !relinkTarget}
                                  onClick={() => void relink(relinkTarget)}>Перенести</button>
                          <button className="btn small" disabled={relinkBusy}
                                  onClick={() => void relink(undefined)}>Во входящие</button>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </label>
            </div>
          );
        })}
        {selected.size > 0 && (
          <div className="inline-form">
            <div className="form-row" style={{ alignItems: 'flex-end' }}>
              <Field label={`Привязать выбранные (${selected.size}) к заявке`}>
                <LinkTargetSelect value={linkTarget} onChange={setLinkTarget} currentRequestId={requestId} />
              </Field>
              <button className="btn primary" disabled={linkBusy || !linkTarget}
                      onClick={() => void linkSelected()}>
                {linkBusy ? 'Привязка…' : 'Привязать выбранные'}
              </button>
            </div>
          </div>
        )}
      </Card>

      <Card title="Ответ клиенту">
        <ErrorBox error={replyErr} />
        {replyNotice && <div className="alert alert-info">{replyNotice}</div>}
        <Field label="Текст ответа">
          <textarea rows={4} value={replyBody} onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Ответ отправляется в активный канал диалога заявки" />
        </Field>

        <Field label={`Вложения (до ${MAX_ATTACHMENTS} файлов · до ${MAX_FILE_MB} МБ каждый)`}>
          <div className={`dropzone ${dragOver ? 'over' : ''}`}
               onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
               onDragLeave={() => setDragOver(false)}
               onDrop={(e) => {
                 e.preventDefault(); setDragOver(false);
                 addDropped(e.dataTransfer.files);
               }}
               onClick={() => document.getElementById('reply-file-input')?.click()}>
            Перетащите файлы сюда или выберите на компьютере
            <input id="reply-file-input" type="file" multiple style={{ display: 'none' }}
                   onChange={(e) => {
                     if (e.target.files) addDropped(e.target.files);
                     e.target.value = '';
                   }} />
          </div>
        </Field>

        {dropped.map((d) => (
          <div key={`${d.file.name}-${d.file.size}`} className="attach-card">
            <span style={{ minWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={d.file.name}>{d.file.name}</span>
            <span className="muted">{(d.file.size / 1024 / 1024).toFixed(1)} МБ</span>
            {d.status === 'uploading' && (
              <span className="bar"><div style={{ width: `${d.progress}%` }} /></span>
            )}
            {d.status === 'checking' && <span className="muted">проверка…</span>}
            {d.status === 'ready' && <span className="badge info">готов</span>}
            {d.status === 'error' && (
              <>
                <span className="badge danger">{d.error ?? 'ошибка'}</span>
                <button className="btn small" onClick={() => retryDropped(d.file)}>Повторить</button>
              </>
            )}
            <button className="btn small" onClick={() => removeDropped(d.file)}>✕</button>
          </div>
        ))}
        {droppedPending && (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Вложения ещё загружаются или проверяются — отправка доступна после завершения.
          </div>
        )}

        {(blobs.data ?? []).length > 0 && (
          <Field label="Вложения из файлов заявки">
            <div>
              {(blobs.data ?? []).map((b) => (
                <label key={b.id} style={{ display: 'inline-flex', gap: 4, marginRight: 12 }}>
                  <input type="checkbox"
                         checked={replyAtts.has(b.id)}
                         onChange={() => setReplyAtts((prev) => {
                           const next = new Set(prev);
                           if (next.has(b.id)) next.delete(b.id); else next.add(b.id);
                           return next;
                         })} />
                  {b.original_name ?? b.filename ?? b.name ?? shortId(b.id)}
                </label>
              ))}
            </div>
          </Field>
        )}
        <div className="form-actions">
          <button className="btn primary" disabled={replyBusy || !replyBody.trim() || droppedPending}
                  title={droppedPending ? 'Дождитесь завершения загрузки и проверки вложений' : ''}
                  onClick={() => void sendReply()}>
            {replyBusy ? 'Отправка…' : 'Отправить'}
          </button>
        </div>
      </Card>
      {previewIdx && (
        <FilePreviewModal items={previewIdx.items} index={previewIdx.index}
                          onClose={() => setPreviewIdx(null)}
                          onNavigate={(i) => setPreviewIdx((p) => p && { ...p, index: i })} />
      )}
    </div>
  );
}

/** Выбор целевой заявки для ручной привязки сообщений. */
function LinkTargetSelect({ value, onChange, currentRequestId }: {
  value: string; onChange: (v: string) => void; currentRequestId: string;
}) {
  const list = useAsync<RequestEntity[]>(() => requests.list(), []);
  const rows = list.data ?? [];
  const hasCurrent = rows.some((r) => r.id === currentRequestId);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {!hasCurrent && <option value={currentRequestId}>Текущая заявка</option>}
      {rows.map((r) => (
        <option key={r.id} value={r.id}>
          {r.number ?? shortId(r.id)}{r.id === currentRequestId ? ' (текущая)' : ''}
        </option>
      ))}
    </select>
  );
}
